import type { JSX } from 'solid-js';
import { createMemo, Show } from 'solid-js';
import { ApprovalsList } from '../components/ApprovalsList';
import { EmptyState } from '../components/EmptyState';
import { FleetTable } from '../components/FleetTable';
import { LiveAuditFeed } from '../components/LiveAuditFeed';
import { OnboardingNudge } from '../components/OnboardingNudge';
import { PageHeader } from '../components/PageHeader';
import { Pager } from '../components/Pager';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { SessionBadge } from '../components/SessionBadge';
import { Skeleton } from '../components/Skeleton';
import { StatBand } from '../components/StatBand';
import { StatusPill } from '../components/StatusPill';
import { buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import type { AccountStreamStatus } from '../lib/accountStream';
import { useApprovalQueue } from '../lib/approval-queue';
import { useFleetUtilization } from '../lib/fleetUtilization';
import { GH_APP_INSTALL_URL, useOnboardingState } from '../lib/onboarding';
import { orgHref } from '../lib/org';
import {
  type AccountSession,
  inFlightCount,
  toFleetRows,
  useLiveAccountSessions,
} from '../lib/overview';
import { useOverviewBurn, useOverviewCounts } from '../lib/overview-counts';
import { pendingSummary, statTiles } from '../lib/overview-tiles';
import { createPagination } from '../lib/paginate';
import { useRepoList } from '../lib/repos';
import { SESSION_TOKEN, useMe } from '../lib/session';

// Home overview (canvas APP artboard). Container — joins the account-wide reads
// and hands the presenters resolved rows, top to bottom: the four-tile metric
// strip, the Fleet table beside the Audit feed, and the Pending actions queue.
// Every tile is live-sourced or "—" (`overview-tiles.ts`); the band never blanks.

/** The feed's connection chip; `idle` (no stream dialled) renders none. */
const STREAM_LABEL: Record<AccountStreamStatus, string | null> = {
  idle: null,
  connecting: 'app.connecting',
  live: 'overview.feed_live',
  reconnecting: 'app.reconnecting',
  error: 'app.error',
};

/** `token` is injectable for tests; the router mounts this with no props → dev session. */
export function Home(props: { token?: string | null } = {}): JSX.Element {
  // Only an OMITTED prop means the dev session: `??` would turn an explicit
  // `null` (signed out) back into `SESSION_TOKEN`.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const me = useMe(token);
  // Every enabled repo, not page one: the fleet table lists them all.
  const repos = useRepoList(token);
  const onboarding = useOnboardingState(token);
  const counts = useOverviewCounts(token);
  const burn = useOverviewBurn(token);
  const utilization = useFleetUtilization(token);
  // Live overview (#855, #7612): the `sessions` and `approvals` topics re-read
  // their REST reads (`createLiveQuery`); what renders is always the read.
  const live = useLiveAccountSessions(token);
  const sessions = live.query;
  const sessionRows = (): readonly AccountSession[] => sessions.data ?? [];
  // The approval queue + its decision: the one hook Pending renders too.
  const { approvals, rows: queueRows, decision, decide, mayDecide } = useApprovalQueue(token);
  const hasSession = token !== null;
  /** Setup alert only once the funnel resolves incomplete. */
  const midFunnel = (): boolean => onboarding.data !== undefined && !onboarding.data.complete;
  // Mid-funnel (or funnel unresolved) the nudge owns the setup CTA — the empty
  // fleet panel keeps its message but doesn't repeat the install link.
  const installCta = (): boolean => onboarding.data?.complete ?? false;

  // Every panel below pages rows the client ALREADY holds in full (bounded
  // top-50 slices, and the repo list walked to its last cursor), so a "Load
  // more" would have nothing to load (`lib/paginate.ts`).
  // `working` is box-held only (a box's running `jobs`, #6745); a session alone is `active`.
  const fleetRows = createMemo(() =>
    toFleetRows(repos.rows(), sessionRows(), utilization.data?.boxes ?? []),
  );
  const fleetPage = createPagination(fleetRows, 6);
  const auditPage = createPagination(sessionRows, 7);
  const approvalsPage = createPagination(queueRows, 5);

  // A refused or failed source is `undefined` data, so its tile reads "—".
  const tiles = () =>
    statTiles({
      counts: counts.data,
      burn: burn.data,
      boxes: utilization.data?.boxes,
      liveWork: inFlightCount(sessions.data),
    });

  const viewAll = (path: string, label: string) => (
    <a class={buttonClass({ variant: 'link' })} href={orgHref(path)} aria-label={label}>
      {t('overview.view_all')}
    </a>
  );

  return (
    <>
      <PageHeader
        title={t('home.title')}
        desc={t('home.subtitle')}
        actions={<SessionBadge login={me.data?.github_login ?? null} loading={me.isLoading} />}
      />

      <Show when={hasSession} fallback={<EmptyState message={t('home.no_session')} />}>
        <Show when={midFunnel()}>
          <OnboardingNudge />
        </Show>

        <StatBand tiles={tiles()} />

        <div class="home-grid">
          <Panel
            class="home-panel"
            title={t('overview.fleet_heading')}
            action={viewAll('/fleet', t('overview.view_all_fleet'))}
          >
            <QueryBoundary
              query={repos.view}
              isEmpty={(rows) => rows.length === 0}
              loading={<Skeleton height="14rem" />}
              empty={
                <EmptyState
                  message={t('repos.empty')}
                  {...(installCta()
                    ? { ctaLabel: t('repos.install_cta'), ctaHref: GH_APP_INSTALL_URL }
                    : {})}
                />
              }
            >
              {() => (
                <>
                  <FleetTable rows={fleetPage.slice()} />
                  <Pager pagination={fleetPage} label={t('overview.fleet_heading')} />
                </>
              )}
            </QueryBoundary>
          </Panel>

          <Panel
            class="home-panel"
            title={t('overview.audit_heading')}
            action={
              <div class="home-panel__actions">
                <Show when={STREAM_LABEL[live.status()]}>
                  {(key) => (
                    <StatusPill tone={live.isLive() ? 'live' : 'neutral'}>{t(key())}</StatusPill>
                  )}
                </Show>
                {viewAll('/audit', t('overview.view_all_audit'))}
              </div>
            }
          >
            <QueryBoundary
              query={sessions}
              isEmpty={() => sessionRows().length === 0}
              loading={<Skeleton height="14rem" />}
              empty={<p class="list-empty">{t('overview.audit_empty')}</p>}
            >
              {() => (
                <>
                  <LiveAuditFeed sessions={auditPage.slice()} />
                  <Pager pagination={auditPage} label={t('overview.audit_heading')} />
                </>
              )}
            </QueryBoundary>
          </Panel>
        </div>

        <Panel
          class="home-panel home-pending"
          title={t('pending.title')}
          action={
            <div class="home-panel__actions">
              <Show when={pendingSummary(queueRows())}>
                {(line) => <span class="pending-actions__summary">{line()}</span>}
              </Show>
              {viewAll('/pending', t('pending.view_all'))}
            </div>
          }
        >
          <Show when={decision.error()}>
            {(message) => (
              <div class="alert alert--destructive" role="alert">
                <p class="alert__title">{message()}</p>
              </div>
            )}
          </Show>
          <QueryBoundary
            query={approvals}
            // The rows on screen: a decided row hidden while a re-read still
            // lists it does not count as waiting.
            isEmpty={() => queueRows().length === 0}
            loading={<Skeleton height="10rem" />}
            empty={<p class="list-empty">{t('approvals.empty')}</p>}
          >
            {() => (
              <>
                <ApprovalsList
                  rows={approvalsPage.slice()}
                  busy={decision.pending()}
                  mayDecide={mayDecide()}
                  onDecide={decide}
                />
                <Pager pagination={approvalsPage} label={t('pending.title')} />
              </>
            )}
          </QueryBoundary>
        </Panel>
      </Show>
    </>
  );
}
